/**
 * "Home Free Cassette" (ENG-2291), look 4a of the Home Free crowd light show
 * room simulation, the finale: every phone shows the band's cassette on black.
 * From the stage the crowd reads as a field of warm yellow.
 */

type Ctx = CanvasRenderingContext2D;

/** Share of the screen's width (or height, if that's tighter) it fills. */
const SIZE = 0.8;

// The cassette is a large image, so apps load it on demand through
// setCassetteLoader; until it's in, the screen stays black.
let loadCassette: (() => Promise<string>) | undefined;
let cassette: HTMLImageElement | null | undefined;

/**
 * How this app loads the cassette image (`@max/common/lightshow/cassette`).
 * Register it once with a dynamic import, e.g.
 * `setCassetteLoader(() => import("@max/common/lightshow/cassette").then((m) => m.CASSETTE_IMAGE))`,
 * so the app's bundler keeps the image in its own chunk.
 */
export const setCassetteLoader = (loader: () => Promise<string>) => {
  loadCassette = loader;
};

/** The cassette once it's loaded; starts loading it on the first call. */
export const getCassette = () => {
  if (cassette !== undefined) return cassette;
  if (!loadCassette || typeof Image === "undefined") return null;
  cassette = null;
  loadCassette()
    .then((src) => {
      const image = new Image();
      image.onload = () => {
        cassette = image;
      };
      image.onerror = () => {
        cassette = undefined;
      };
      image.src = src;
    })
    .catch(() => {
      // Try again on a later frame, e.g. after a dropped connection.
      cassette = undefined;
    });
  return null;
};

export const renderHomeFreeCassette = (ctx: Ctx, w: number, h: number) => {
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, w, h);
  const image = getCassette();
  if (!image) return;
  const scale = Math.min(
    (w * SIZE) / image.naturalWidth,
    (h * SIZE) / image.naturalHeight,
  );
  const iw = image.naturalWidth * scale;
  const ih = image.naturalHeight * scale;
  ctx.drawImage(image, (w - iw) / 2, (h - ih) / 2, iw, ih);
};
